import { CloseButton, Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ArrowLeft, X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";
import { Button } from "./Button";

const backdrop =
  "fixed inset-0 bg-inverse-surface/40 backdrop-blur-[2px] transition-opacity duration-200 ease-out data-closed:opacity-0";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** Mobile presentation: bottom sheet (default) or full screen with sticky footer. */
  mobile?: "sheet" | "fullscreen";
  footer?: ReactNode;
  children?: ReactNode;
  bodyClassName?: string;
  /** Prevent closing by backdrop/Escape (e.g. while saving). */
  locked?: boolean;
}

const widths = { sm: "md:max-w-md", md: "md:max-w-lg", lg: "md:max-w-2xl", xl: "md:max-w-4xl" };

/** Dialog on desktop, bottom sheet or full-screen sheet on mobile (plan: "Input Jadwal", "Ubah Jadwal"). */
export function Modal({
  open,
  onClose,
  title,
  description,
  icon,
  size = "md",
  mobile = "sheet",
  footer,
  children,
  bodyClassName,
  locked,
}: ModalProps) {
  const fullscreen = mobile === "fullscreen";
  return (
    <Dialog open={open} onClose={() => (locked ? undefined : onClose())} className="relative z-[60]">
      <DialogBackdrop transition className={backdrop} />
      <div className={cn("fixed inset-0 flex justify-center md:items-center md:p-6", fullscreen ? "items-stretch" : "items-end")}>
        <DialogPanel
          transition
          className={cn(
            "relative flex w-full flex-col overflow-hidden bg-surface-container-lowest shadow-pop transition duration-300 ease-out-soft",
            "data-closed:translate-y-8 data-closed:opacity-0 md:data-closed:translate-y-2 md:data-closed:scale-[0.98]",
            fullscreen ? "h-dvh md:h-auto md:max-h-[88vh] md:rounded-card" : "max-h-[92dvh] rounded-t-[22px] md:max-h-[88vh] md:rounded-card",
            widths[size],
          )}
        >
          {!fullscreen && (
            <span aria-hidden className="absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-outline-variant md:hidden" />
          )}
          <header className={cn("flex items-start gap-3 border-b border-outline-variant/40 px-5 pb-4", fullscreen ? "pt-safe pt-4" : "pt-6 md:pt-4")}>
            {icon && (
              <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary-fixed text-primary [&_svg]:size-5">
                {icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-headline-sm text-on-surface">{title}</DialogTitle>
              {description && <Description className="mt-0.5 text-body-sm text-on-surface-variant">{description}</Description>}
            </div>
            {!locked && (
              <CloseButton
                aria-label="Close"
                className="-mr-1.5 inline-flex size-9 shrink-0 items-center justify-center rounded-control text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
              >
                <X className="size-5" />
              </CloseButton>
            )}
          </header>
          {children != null && children !== false && (
            <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5", bodyClassName)}>{children}</div>
          )}
          {footer && (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-outline-variant/40 bg-surface-container-low/60 px-5 pt-3.5 pb-[max(env(safe-area-inset-bottom),0.875rem)] [&>button]:max-md:flex-1">
              {footer}
            </footer>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  headerActions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  width?: "md" | "lg";
}

/** Right-side panel on desktop (~440px, page stays visible); full screen with a back button on mobile. */
export function Drawer({ open, onClose, title, subtitle, icon, headerActions, footer, children, width = "md" }: DrawerProps) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-inverse-surface/25 transition-opacity duration-300 ease-out data-closed:opacity-0"
      />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel
          transition
          className={cn(
            "flex h-dvh w-full flex-col bg-surface-container-lowest shadow-pop transition duration-300 ease-out-soft data-closed:translate-x-full md:border-l md:border-outline-variant/50",
            width === "md" ? "md:w-[440px]" : "md:w-[560px]",
          )}
        >
          <header className="pt-safe flex items-center gap-3 border-b border-outline-variant/40 bg-surface-container-low/80 px-4 py-3 md:px-5 md:py-4">
            <CloseButton
              aria-label="Back"
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-control text-on-surface-variant hover:bg-surface-container md:hidden"
            >
              <ArrowLeft className="size-5" />
            </CloseButton>
            {icon && (
              <div className="hidden size-9 shrink-0 items-center justify-center rounded-xl bg-primary-container text-on-primary md:flex [&_svg]:size-[18px]">
                {icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-headline-sm text-on-surface">{title}</DialogTitle>
              {subtitle && <p className="truncate text-label-sm text-on-surface-variant">{subtitle}</p>}
            </div>
            {headerActions}
            <CloseButton
              aria-label="Close panel"
              className="hidden size-9 shrink-0 items-center justify-center rounded-control text-on-surface-variant hover:bg-surface-container hover:text-error md:inline-flex"
            >
              <X className="size-5" />
            </CloseButton>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
          {footer && (
            <footer className="border-t border-outline-variant/40 bg-surface-container-lowest px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] md:px-5">
              {footer}
            </footer>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger";
  loading?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  loading,
  icon,
  children,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      icon={icon}
      size="sm"
      locked={loading}
      footer={
        <>
          <Button variant="tonal" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === "danger" ? "danger-solid" : "primary"} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
